'use client';

import { FC, useContext } from 'react';
import { Flex, Separator, Tooltip, Text, Counter } from '@/_components';
import { SignatureCountCellProps } from './SignatureCountCell.types';
import { isEmpty } from '@/_utils/strings';
import {
  getTotalSignatureCount,
  getCompletedSignatureCount,
  getRemainingSignatureCount,
} from './SignatureCountCell.utils';
import { AdminTaxReturnsContext } from '@/_contexts/admin/AdminTaxReturnsProvider';
import styles from './SignatureCountCell.module.css';

export const SignatureCountCell: FC<SignatureCountCellProps> = ({
  taxReturn,
}) => {
  const { formDefinitions } = useContext(AdminTaxReturnsContext);
  const completedSignatureCount: number = getCompletedSignatureCount(taxReturn);
  const allSignatureCount: number = getTotalSignatureCount(taxReturn);
  const remainingSignatureCount: number = getRemainingSignatureCount(taxReturn);

  const getTooltipContent = (): React.ReactNode => {
    if (taxReturn?.total_signature_count === 0) {
      return <>No signatures requested.</>;
    }

    if (allSignatureCount && allSignatureCount === completedSignatureCount) {
      return <>All signatures completed</>;
    }

    const listTitle: string = `${remainingSignatureCount} signature${remainingSignatureCount === 1 ? '' : 's'} still needed on these forms:`;
    const listItems: Array<React.ReactNode> = [];

    const unSignedForms: Array<any> = taxReturn?.e_sign_forms?.filter(
      (v: any) => !v.signatureData,
    );

    for (const form of unSignedForms) {
      if (form.form_id === 'other' && !isEmpty(form.custom_form_name)) {
        listItems.push(<>{form.custom_payment_name}</>);
      } else {
        const formname = formDefinitions.find(
          (f: any) => f.id === form.form_id,
        );

        formname &&
          listItems.push(
            <>
              {formname?.name} (Form {form.form_id})
            </>,
          );
      }
    }

    return (
      <Flex isColumn={true} gap={2}>
        {listTitle}
        <Separator isInverted={true} />
        <ul>
          {listItems?.map((item, key) => {
            return (
              <li className={styles.listItem} key={key}>
                {item}
              </li>
            );
          })}
        </ul>
      </Flex>
    );
  };

  return (
    <Tooltip>
      <Tooltip.Content>
        <Text type="tiny" color="currentcolor">
          {getTooltipContent()}
        </Text>
      </Tooltip.Content>
      <Tooltip.Trigger>
        <Counter
          doneCount={completedSignatureCount}
          allCount={allSignatureCount}
        />
      </Tooltip.Trigger>
    </Tooltip>
  );
};
